/**
 * WeatherSystem - Procedural particle-based weather effects.
 * Covers tasks 6.7 (rain/snow particles), 4.10 (weather sounds).
 */

import * as BABYLON from "babylonjs";
import { AudioSystem } from "./AudioSystem";

export type WeatherType = "none" | "rain" | "snow";

export class WeatherSystem {
  private _scene: BABYLON.Scene;
  private _particleSystem: BABYLON.ParticleSystem | null = null;
  private _currentWeather: WeatherType = "none";

  constructor(scene: BABYLON.Scene) {
    this._scene = scene;
  }

  setWeather(type: WeatherType): void {
    if (type === this._currentWeather) return;
    this._clear();
    this._currentWeather = type;

    switch (type) {
      case "rain":
        this._startRain();
        AudioSystem.startRainSound();
        break;
      case "snow":
        this._startSnow();
        break;
      case "none":
        AudioSystem.stopWeatherSound();
        break;
    }
  }

  getWeather(): WeatherType {
    return this._currentWeather;
  }

  private _clear(): void {
    if (this._particleSystem) {
      this._particleSystem.stop();
      this._particleSystem.dispose();
      this._particleSystem = null;
    }
    AudioSystem.stopWeatherSound();
  }

  private _startRain(): void {
    const ps = new BABYLON.ParticleSystem("rain", 3000, this._scene);

    // Rain emitter — large plane above field
    const emitter = BABYLON.MeshBuilder.CreateBox("rainEmitter", { size: 0.01 }, this._scene);
    emitter.isVisible = false;
    emitter.position.y = 25;

    ps.emitter = emitter;
    ps.minEmitBox = new BABYLON.Vector3(-25, 0, -35);
    ps.maxEmitBox = new BABYLON.Vector3(25, 0, 35);

    // Textures (use a small white dot)
    ps.particleTexture = new BABYLON.Texture(
      "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAQAAAAICAYAAADeM14FAAAAGklEQVQI12NgYGD4z8BQDwAEgAF/QualIQAAAABJRU5ErkJggg==",
      this._scene
    );

    ps.color1 = new BABYLON.Color4(0.6, 0.7, 1.0, 0.6);
    ps.color2 = new BABYLON.Color4(0.5, 0.6, 0.9, 0.4);
    ps.colorDead = new BABYLON.Color4(0, 0, 0.5, 0.0);

    ps.minSize = 0.03;
    ps.maxSize = 0.08;
    ps.minLifeTime = 0.8;
    ps.maxLifeTime = 1.5;
    ps.emitRate = 2000;

    ps.blendMode = BABYLON.ParticleSystem.BLENDMODE_ADD;
    ps.gravity = new BABYLON.Vector3(0, -25, 0);

    ps.direction1 = new BABYLON.Vector3(-1, -8, 0.5);
    ps.direction2 = new BABYLON.Vector3(1, -8, -0.5);

    ps.minAngularSpeed = 0;
    ps.maxAngularSpeed = 0;
    ps.minEmitPower = 1;
    ps.maxEmitPower = 2;
    ps.updateSpeed = 0.02;

    ps.start();
    this._particleSystem = ps;
  }

  private _startSnow(): void {
    const ps = new BABYLON.ParticleSystem("snow", 1500, this._scene);

    const emitter = BABYLON.MeshBuilder.CreateBox("snowEmitter", { size: 0.01 }, this._scene);
    emitter.isVisible = false;
    emitter.position.y = 20;

    ps.emitter = emitter;
    ps.minEmitBox = new BABYLON.Vector3(-25, 0, -35);
    ps.maxEmitBox = new BABYLON.Vector3(25, 0, 35);

    ps.particleTexture = new BABYLON.Texture(
      "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAgAAAAICAYAAADED76LAAAAMElEQVQY02NgGAWkgv///w8Q0gwA+f///5MgGgCqBhkgKkEFgBRSEKgIqJVkFQAAgAcTtpTxcuAAAAAASUVORK5CYII=",
      this._scene
    );

    ps.color1 = new BABYLON.Color4(1, 1, 1, 0.9);
    ps.color2 = new BABYLON.Color4(0.9, 0.9, 1, 0.7);
    ps.colorDead = new BABYLON.Color4(1, 1, 1, 0.0);

    ps.minSize = 0.08;
    ps.maxSize = 0.2;
    ps.minLifeTime = 3;
    ps.maxLifeTime = 6;
    ps.emitRate = 300;

    ps.blendMode = BABYLON.ParticleSystem.BLENDMODE_STANDARD;
    ps.gravity = new BABYLON.Vector3(0, -2, 0);

    ps.direction1 = new BABYLON.Vector3(-0.5, -1, 0.3);
    ps.direction2 = new BABYLON.Vector3(0.5, -1, -0.3);

    ps.minAngularSpeed = -0.5;
    ps.maxAngularSpeed = 0.5;
    ps.minEmitPower = 0.2;
    ps.maxEmitPower = 0.8;
    ps.updateSpeed = 0.01;

    ps.start();
    this._particleSystem = ps;
  }

  dispose(): void {
    this._clear();
  }
}
